import React from 'react';

import ReactGA from 'react-ga4';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    AnalyticsCategories,
    COMPLEMENT_MODULES,
    HistoryRoutes,
    INITIAL_MODULES,
    Messages,
    SnackbarVariants,
    USER_DATA,
} from 'commons/constants';
import {functions, messageFunctions} from 'commons/utils';
import LoginForm from './login-form/LoginForm';
import {useAnalytics} from 'hooks/AnalyticsHook';
import {KeycloakService, UsersService} from 'services';
import {useUserContext} from 'context/user/UserContext';
import {KeycloakTokenResponse} from 'commons/types/Types';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {
    CreateAccountValues,
    EfeResponse,
    LoginValues,
    RecoveryTokenResponse,
    UserRegistrationResponse,
} from 'commons/models';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {useBeneficiaryContext} from 'context/beneficiary-context/BeneficiaryContext';
import {EfeModal, EfeModalMsg} from 'components';
import CreateForm from './create-form/CreateForm';
import {useAlert} from 'hooks';
import {REACT_APP_GA_ID} from 'commons/env/env-vars';

function Login() {
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const history = useHistory();
    const {updateMenu} = useMenuContext();
    const {enqueueSnackbar} = useSnackbar();
    const {user, updateUser} = useUserContext();
    const {sendPageView, sendEvent} = useAnalytics();
    const {
        setPercentageCompleted,
        setRealOwners,
        setRepresentatives,
        setShareholders,
        setTempRealOwner,
        setTempRepresentative,
        setTempShareholder,
        setTempRealOwnerActive,
        setTempShaholderActive,
        setShareholdersSizeTemp,
    } = useRelatedPeopleContext();
    const {resetBeneficiaryContext} = useBeneficiaryContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<boolean>(false);

    async function createAccount(values: CreateAccountValues) {
        setIsLoading(true);
        try {
            console.log({...values});
            const response: EfeResponse<RecoveryTokenResponse> =
                await KeycloakService.createAccount({...values});
            if (response && response.data && response.data.message) {
                setOpen(!open);
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        } finally {
            setIsLoading(false);
        }
    }

    async function getToken(values: LoginValues, userData: UserRegistrationResponse) {
        let message = messageFunctions.getAnalyticsMessage(
            `${AnalyticsCategories.LOGIN} Error`,
            values.username.toUpperCase()
        );
        try {
            const token: EfeResponse<KeycloakTokenResponse> = await KeycloakService.getToken(
                values
            );
            if (token && token.data && token.data.access_token) {
                const expirationTime = new Date(
                    new Date().getTime() + token.data.expires_in * 1000
                );
                updateMenu([...INITIAL_MODULES, ...COMPLEMENT_MODULES]);
                USER_DATA.email = userData.email;
                USER_DATA.pendingActions = user.pendingActions;
                USER_DATA.idSalesforce = userData.idSalesforce;
                USER_DATA.username = values.username.toUpperCase();

                USER_DATA.loggedIn = true;
                USER_DATA.token = token.data.access_token;
                USER_DATA.expirationDate = expirationTime;
                USER_DATA.tokenType = token.data.token_type;
                USER_DATA.expires = token.data.expires_in * 1000;
                USER_DATA.refreshToken = token.data.refresh_token;

                USER_DATA.fullName = userData.fullName ? userData.fullName : '';
                USER_DATA.isMigrated = userData.isMigrated;
                USER_DATA.contractService = userData.contractService;
                updateUser({
                    username: values.username.toUpperCase(),
                    idSalesforce: userData.idSalesforce,
                    email: userData.email,
                    fullName: userData.fullName ? userData.fullName : '',
                    isMigrated: userData.isMigrated,
                    loggedIn: true,
                    tokenType: token.data.token_type,
                    token: token.data.access_token,
                    expires: token.data.expires_in * 1000,
                    refreshToken: token.data.refresh_token,
                    pendingActions: user.pendingActions,
                    expirationDate: expirationTime,
                    logout: false,
                    contractService: userData.contractService,
                    isAdmin: functions.isAdmin(userData.idSalesforce),
                });
                message = messageFunctions.getAnalyticsMessage(
                    AnalyticsCategories.LOGIN,
                    values.username.toUpperCase()
                );
                setIsLoading(false);
                resetRelatedPeopleContext();
                resetBeneficiaryContext();
                history.push(HistoryRoutes.CLIENT_STATUS);
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.TOKEN_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            enqueueSnackbar(Messages.TOKEN_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
            setIsLoading(false);
        }
        sendEvent(AnalyticsCategories.LOGIN, message);
    }

    async function onSubmit(values: LoginValues) {
        try {
            setIsLoading(true);
            const response: EfeResponse<UserRegistrationResponse> =
                await UsersService.userRegistration({
                    username: values.username.toUpperCase(),
                    password: values.password,
                });
            if (response && response.data && response.data.email) {
                await getToken(values, response.data);
            } else {
                setIsLoading(false);
                enqueueSnackbar(Messages.LOGIN_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    function resetRelatedPeopleContext() {
        setPercentageCompleted(0);
        setRealOwners([]);
        setShareholders([]);
        setRepresentatives([]);
        setTempRealOwner(undefined);
        setTempRepresentative(undefined);
        setTempShareholder(undefined);
        setTempRealOwnerActive(false);
        setTempShaholderActive(false);
        setShareholdersSizeTemp(1);
    }

    React.useEffect(() => {
        ReactGA.initialize([
            {
                trackingId: `${REACT_APP_GA_ID}`,
            },
        ]);
        sendPageView(HistoryRoutes.LOGIN);
    }, []);

    return (
        <>
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {open && (
                <EfeModal
                    open={open}
                    title={'Crear cuenta'}
                    reactElement={
                        <CreateForm
                            title={'Ingresa los siguientes datos'}
                            isLoading={isLoading}
                            submitHandler={createAccount}
                        />
                    }
                    handleAction={() => setOpen(!open)}
                />
            )}
            <LoginForm
                submitHandler={onSubmit}
                isLoading={isLoading}
                createUser={() => setOpen(!open)}
            />
            ;
        </>
    );
}

export default Login;
